// utils.js — pure JS helpers for CrossStitch Designer

// Clamp a value
export const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

// Device pixel ratio (fallback 1)
export const getDPR = () => (typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1);

// Resize canvas to match its CSS size (with DPR)
export function resizeCanvasToDisplaySize(canvas) {
  const dpr = getDPR();
  const { clientWidth: w, clientHeight: h } = canvas;
  const needResize = canvas.width !== Math.floor(w * dpr) || canvas.height !== Math.floor(h * dpr);
  if (needResize) {
    canvas.width = Math.floor(w * dpr);
    canvas.height = Math.floor(h * dpr);
  }
  const ctx = canvas.getContext("2d");
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  return needResize;
}

// Deep copy layers (typed arrays)
export function deepCopyLayers(src) {
  return src.map((L) => ({ ...L, cells: new Int32Array(L.cells) }));
}

// Empty layer factory
export function createEmptyLayer(id, name, cols, rows) {
  return {
    id,
    name,
    visible: true,
    opacity: 1,
    cells: new Int32Array(cols * rows).fill(-1),
  };
}

// History stack
export class HistoryStack {
  constructor(limit = 100) {
    this.limit = limit;
    this.past = [];
    this.future = [];
  }
  push(state) {
    this.past.push(state);
    if (this.past.length > this.limit) this.past.shift();
    this.future = [];
  }
  canUndo() {
    return this.past.length > 0;
  }
  canRedo() {
    return this.future.length > 0;
  }
  undo(current) {
    if (!this.canUndo()) return current;
    const prev = this.past.pop();
    this.future.push(current);
    return prev;
  }
  redo(current) {
    if (!this.canRedo()) return current;
    const next = this.future.pop();
    this.past.push(current);
    return next;
  }
}

// Convert client (px) to grid index
export function clientToIndex(canvas, x, y, cols, rows, cellSize, zoom, offset) {
  const rect = canvas.getBoundingClientRect();
  const cs = cellSize * zoom;
  const gx = Math.floor((x - rect.left - offset.x) / cs);
  const gy = Math.floor((y - rect.top - offset.y) / cs);
  if (gx < 0 || gy < 0 || gx >= cols || gy >= rows) return null;
  return gy * cols + gx;
}

// Suggest pixel size per cell from Aida count
export function suggestedCellSize(aidaCount) {
  // Keeps UI comfortable: lower Aida ⇒ bigger cell, higher ⇒ smaller
  // Tuned for tablets
  return clamp(Math.round(280 / (aidaCount || 14)), 6, 28);
}
